/**
 * Common bulk-generate form field configuration.
 *
 * Each prompt form can declare per-slug BulkFormConfig entries that describe
 * the inline option fields rendered inside BulkGenerateModal.
 * Types and the central registry live here so they are form-agnostic.
 */

import type { PromptTranslations } from './i18n';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BulkFormFieldType = 'select' | 'number';

export interface BulkFormFieldOption {
    label: string;
    value: string | number;
}

export interface BulkFormField {
    key: string;
    label: string;
    /** EAA-2026: prompt-translation key; consumers show it instead of the raw ``label``. */
    labelKey?: keyof PromptTranslations;
    type: BulkFormFieldType;
    defaultValue: string | number;
    options?: BulkFormFieldOption[];
    min?: number;
    max?: number;
}

export interface BulkFormConfig {
    fields: BulkFormField[];
}

// ---------------------------------------------------------------------------
// Reusable field presets
// ---------------------------------------------------------------------------

export const MEDIA_FIELD: BulkFormField = {
    key: 'media',
    label: 'programContent.mediaLabel',
    type: 'select',
    // WHY: Agent media enum is none|passage|image|audio only (SSOT §5) — no video.
    defaultValue: 'none',
    options: [
        { label: 'programContent.mediaNoMedia', value: 'none' },
        { label: 'Passage', value: 'passage' },
        { label: 'Image', value: 'image' },
        { label: 'Audio', value: 'audio' },
    ],
};

export const NUM_BLANKS_FIELD: BulkFormField = {
    key: 'num_blanks',
    label: 'programContent.blanksPerQuestion',
    type: 'number',
    defaultValue: 1,
    min: 1,
    max: 15,
};

// ---------------------------------------------------------------------------
// Registry — all slug → BulkFormConfig mappings are collected here
// ---------------------------------------------------------------------------

const bulkFormConfigRegistry: Record<string, BulkFormConfig> = {};

/**
 * Register bulk form configs for one or more slugs.
 * Called by each form module at import-time (side-effect).
 */
export function registerBulkFormConfigs(configs: Record<string, BulkFormConfig>): void {
    Object.assign(bulkFormConfigRegistry, configs);
}

/**
 * Returns a snapshot of all registered bulk form configs.
 * Used by BulkGenerateModal to render inline option fields.
 */
export function getBulkFormConfigs(): Record<string, BulkFormConfig> {
    return { ...bulkFormConfigRegistry };
}
